import type { CompareBy } from "@/metrics/compare-by"
import {
  compareTraceOutputs as compareTraceOutputsData,
  isTraceValuesOutput as isTraceValuesOutputData,
  normalizeTraceOutput as normalizeTraceOutputData,
  traceOutputGroups,
} from "@/metrics/runtime/record-data"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { SetMetricRecord } from "../base/SetMetricRecord"

export function compareTraceOutputs(
  a: Parameters<typeof compareTraceOutputsData>[0],
  b: Parameters<typeof compareTraceOutputsData>[1],
): boolean {
  return compareTraceOutputsData(a, b)
}

export function isTraceValuesOutput(mo: RunMetricOutput): boolean {
  return isTraceValuesOutputData(mo)
}

export function normalizeTraceOutput(output: RunMetricOutput): RunMetricOutput {
  return normalizeTraceOutputData(output)
}

class TraceSetMetricRecord extends SetMetricRecord {
  protected includeEmptyValuesInOverview = true
  protected preserveRawValuesInOverview = true
}

export function traceComparedOverview(
  data: MetricRecordData,
  outputs: RunMetricOutput[],
  runRows: RunRowLike[],
  compareBy: CompareBy,
): React.ReactNode {
  const groups = traceOutputGroups(outputs)
  const renderGroup = (group: RunMetricOutput[]) => {
    const ids = new Set(group.map((output) => output.simulation_output_id))
    const rows = runRows.filter((row) => ids.has(row.simulation_output_id))
    const record = isTraceValuesOutput(group[0])
      ? new TraceSetMetricRecord(data)
      : traceNumericRecord(data, group)
    return record.getComparedOverviewNode(rows, compareBy)
  }
  return renderTraceGroups(groups, renderGroup)
}

export function traceInDepthReview(
  data: MetricRecordData,
  outputs: RunMetricOutput[],
  context?: Parameters<NumericalMetricRecord["renderInDepthRunReview"]>[1],
): React.ReactNode {
  if (!outputs.length) {
    return (
      <div className="text-coval-text-muted">
        No metric data available for this metric.
      </div>
    )
  }
  return renderTraceGroups(traceOutputGroups(outputs), (group) =>
    isTraceValuesOutput(group[0])
      ? new TraceSetMetricRecord(data).renderSetOverview(group, context)
      : traceNumericRecord(data, group).renderInDepthRunReview(group, context),
  )
}

export function traceNumericRecord(
  data: MetricRecordData,
  outputs: RunMetricOutput[],
): NumericalMetricRecord {
  const record = new NumericalMetricRecord({
    ...data,
    output_type: "float",
    organization_threshold: null,
  })
  const unit: unknown = outputs.find(
    (output) => typeof output.result?.unit === "string",
  )?.result?.unit
  record.unit = typeof unit === "string" ? unit : ""
  record.aggregate_units = record.unit
  return record
}

export function traceOverview(
  data: MetricRecordData,
  outputs: RunMetricOutput[],
  options?: Parameters<NumericalMetricRecord["getOverviewNode"]>[1],
): React.ReactNode {
  const groups = traceOutputGroups(outputs)
  const renderGroup = (group: RunMetricOutput[]) => {
    if (isTraceValuesOutput(group[0])) {
      return new TraceSetMetricRecord(data).getOverviewNode(group)
    }
    const numeric = group.filter(
      (output) =>
        output.float_value != null && Number.isFinite(output.float_value),
    )
    if (!numeric.length) {
      return null
    }
    const record = traceNumericRecord(data, numeric)
    return (
      record.getOverviewNode(numeric, options) ??
      record._renderSimulationCard(numeric[0])
    )
  }
  return renderTraceGroups(groups, renderGroup)
}

function renderTraceGroups(
  groups: Map<string, RunMetricOutput[]>,
  renderGroup: (group: RunMetricOutput[]) => React.ReactNode,
): React.ReactNode {
  if (groups.size <= 1) {
    const first = groups.values().next().value
    return first ? renderGroup(first) : null
  }
  return (
    <div className="space-y-2" data-dd-privacy="mask" data-ph-capture="false">
      <p className="text-xs text-coval-text-muted">
        Output types or versions changed. Results are shown separately.
      </p>
      {[...groups.entries()].map(([key, group]) => {
        const first = group[0]
        const version =
          first.metric_version_number != null
            ? `version ${first.metric_version_number}`
            : first.metric_version_id
              ? `version ${first.metric_version_id}`
              : "unversioned"
        const label = `${isTraceValuesOutput(first) ? "Trace value results" : "Numeric trace results"}, ${version}`
        return (
          <section key={key} aria-label={label}>
            <p className="text-xs text-coval-text-muted">{label}</p>
            {renderGroup(group)}
          </section>
        )
      })}
    </div>
  )
}
